import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search,
  Plus,
  ArrowRight,
  Flame,
  Clock,
  Sparkles,
  Handshake,
  UserRound,
  Building2,
  Inbox,
  FileText,
  CheckCircle2,
  AlertTriangle,
  MailPlus,
  Mail,
  Upload,
  Timer,
  ClipboardList,
  FolderSearch,
  Users,
  BookOpen,
  BadgeCheck,
  Activity,
  CircleDot,
  DollarSign,
  MessageSquare,
  Send,
  Filter as FilterIcon,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { useCases, type Case, type CaseStatus } from "@/lib/cases";
import { buildReport, currency } from "@/lib/report";
import { useAllRealReviews, mapDbStatusToCase } from "@/hooks/use-real-reviews";
import { useReviewAIStates, type ReviewAIState } from "@/hooks/use-review-jobs";
import { supabase } from "@/integrations/supabase/client";
import { useRecoveryFacts } from "@/hooks/use-recovery-facts";
import {
  DEMO_NO_FINANCIALS,
  SURFACE_LABELS,
  resolveNegotiationFinancial,
  supportedRecoveryDisplay,
  type NegotiationFinancialDisplay,
} from "@/lib/financial/surface-financials";
import type { Negotiation, Review, TimelineEvent } from "@/lib/db/models";


export const Route = createFileRoute("/operations/")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Operations Center" },
      {
        name: "description",
        content:
          "Internal operations workspace for reviewing invoices, managing negotiations, and coordinating customer activity.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["staff", "admin"]}>
      <OperationsCenterPage />
    </AuthGate>
  ),
});

/* ─────────────────────────────────────────────────────────── */
/* Placeholder data (ready for API integration)                */
/* ─────────────────────────────────────────────────────────── */

type Urgency = "high" | "medium" | "low";

interface NegotiationBrief {
  id: string;
  number: string;
  status: string;
  stage: string;
  specialist: string;
  daysOpen: number;
  /** Canonical financial display — never a percentage estimate (SAB-001). */
  financial: NegotiationFinancialDisplay;
}

interface PriorityItem {
  id: string;
  customer: string;
  vendor: string;
  status: string;
  urgency: Urgency;
  submitted: string;
  progress: number;
  savings: string;
  specialist: string;
  negotiation?: NegotiationBrief;
}

interface NewReview {
  id: string;
  reviewId?: string;
  customer: string;
  vendor: string;
  method: "Photo" | "Upload" | "Email";
  submitted: string;
  status: string;
  priority: Urgency;
  assigned: string;
  negotiation?: NegotiationBrief;
}

interface ActivityItem {
  id: string;
  kind:
    | "submitted"
    | "report"
    | "negotiation"
    | "vendor"
    | "customer"
    | "completed"
    | "neg_purchased"
    | "neg_assigned"
    | "vendor_contacted"
    | "vendor_replied"
    | "settlement"
    | "neg_completed";
  title: string;
  meta: string;
  time: string;
}


interface TeamMember {
  name: string;
  initials: string;
  reviews: number;
  negotiations: number;
  completed: number;
  status: "Available" | "In Review" | "On Call" | "Away";
}

const priorityQueue: PriorityItem[] = [
  {
    id: "INV-2026-0704-0142",
    customer: "Rachel Kim",
    vendor: "SafeMove Storage — Austin",
    status: "Awaiting specialist review",
    urgency: "high",
    submitted: "42 min ago",
    progress: 82,
    savings: DEMO_NO_FINANCIALS,
    specialist: "Unassigned",
  },
  {
    id: "INV-2026-0704-0138",
    customer: "David Ortiz",
    vendor: "Peak Auto Body",
    status: "AI report ready — needs QA",
    urgency: "high",
    submitted: "1h 12m ago",
    progress: 96,
    savings: DEMO_NO_FINANCIALS,
    specialist: "Thomas W.",
  },
  {
    id: "INV-2026-0703-0119",
    customer: "Marta Chen",
    vendor: "Metro Restoration",
    status: "Vendor response received",
    urgency: "medium",
    submitted: "Yesterday",
    progress: 74,
    savings: DEMO_NO_FINANCIALS,
    specialist: "Priya S.",
  },
  {
    id: "INV-2026-0703-0104",
    customer: "James Okafor",
    vendor: "GreenLeaf HVAC",
    status: "Fast-track — 24hr SLA",
    urgency: "high",
    submitted: "Yesterday",
    progress: 58,
    savings: DEMO_NO_FINANCIALS,
    specialist: "Unassigned",
  },
  {
    id: "INV-2026-0702-0088",
    customer: "Ana Reyes",
    vendor: "Coastal Movers Co.",
    status: "Negotiation counter drafted",
    urgency: "medium",
    submitted: "2 days ago",
    progress: 88,
    savings: DEMO_NO_FINANCIALS,
    specialist: "Marcus B.",
  },
];

const newReviews: NewReview[] = [
  {
    id: "INV-2026-0704-0151",
    customer: "Isabella Núñez",
    vendor: "Sunbelt Restoration",
    method: "Email",
    submitted: "8 min ago",
    status: "Received",
    priority: "medium",
    assigned: "Auto-routing",
  },
  {
    id: "INV-2026-0704-0149",
    customer: "Owen Park",
    vendor: "BlueRidge Movers",
    method: "Photo",
    submitted: "23 min ago",
    status: "AI parsing",
    priority: "low",
    assigned: "Unassigned",
  },
  {
    id: "INV-2026-0704-0147",
    customer: "Sasha Grant",
    vendor: "PrecisionAuto Collision",
    method: "Upload",
    submitted: "31 min ago",
    status: "AI parsing",
    priority: "high",
    assigned: "Priya S.",
  },
  {
    id: "INV-2026-0704-0144",
    customer: "Luis Fernández",
    vendor: "AllPro Water Damage",
    method: "Upload",
    submitted: "58 min ago",
    status: "Ready for review",
    priority: "medium",
    assigned: "Thomas W.",
  },
];

const activityFeed: ActivityItem[] = [
  {
    id: "a1",
    kind: "submitted",
    title: "New invoice received",
    meta: "Isabella Núñez • Sunbelt Restoration",
    time: "8m ago",
  },
  {
    id: "a2",
    kind: "report",
    title: "AI findings report completed",
    meta: "INV-2026-0704-0138 • David Ortiz",
    time: "24m ago",
  },
  {
    id: "a3",
    kind: "negotiation",
    title: "Expert Negotiation purchased",
    meta: "Marta Chen • $199 + 25% success fee",
    time: "1h ago",
  },
  {
    id: "a4",
    kind: "vendor",
    title: "Vendor responded to negotiation",
    meta: "Metro Restoration • Counter offer received",
    time: "1h 42m ago",
  },
  {
    id: "a5",
    kind: "customer",
    title: "Customer uploaded supporting documents",
    meta: "James Okafor • 3 files",
    time: "3h ago",
  },
  {
    id: "a6",
    kind: "completed",
    title: "Review completed and delivered",
    meta: "INV-2026-0702-0079 • Saved customer $1,320",
    time: "5h ago",
  },
];

const team: TeamMember[] = [
  {
    name: "Thomas Whitfield",
    initials: "TW",
    reviews: 6,
    negotiations: 2,
    completed: 3,
    status: "In Review",
  },
  {
    name: "Priya Sharma",
    initials: "PS",
    reviews: 4,
    negotiations: 3,
    completed: 2,
    status: "Available",
  },
  {
    name: "Marcus Bell",
    initials: "MB",
    reviews: 5,
    negotiations: 4,
    completed: 1,
    status: "On Call",
  },
  {
    name: "Elena Ruiz",
    initials: "ER",
    reviews: 3,
    negotiations: 1,
    completed: 4,
    status: "Available",
  },
];

const performance = {
  completedToday: 12,
  avgReviewTime: "1h 46m",
  reportsDelivered: 9,
  negotiationsCompleted: 3,
  responsesWaiting: 7,
};

/* ─────────────────────────────────────────────────────────── */
/* Page                                                        */
/* ─────────────────────────────────────────────────────────── */

function OperationsCenterPage() {
  const cases = useCases();
  const {
    reviews: realReviews,
    timeline: realTimeline,
    loading: realLoading,
  } = useAllRealReviews();

  const [negByReviewId, setNegByReviewId] = useState<Record<string, Negotiation>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("negotiations")
        .select("*")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      const map: Record<string, Negotiation> = {};
      for (const n of (data ?? []) as Negotiation[]) {
        // last one wins → newest per review (since ordered desc, first wins)
        if (!map[n.review_id]) map[n.review_id] = n;
      }
      setNegByReviewId(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [realReviews.length]);

  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");

  /* SAB-001: canonical recovery per review — resolved by the same canonical
     resolver the report and dashboard use. Operations never estimates. */
  const reviewIds = useMemo(() => realReviews.map((r) => r.id), [realReviews]);
  const { facts: recoveryFacts } = useRecoveryFacts(reviewIds);
  const recoveryFor = useCallback(
    (id: string): number | null => recoveryFacts.get(id)?.recovery ?? null,
    [recoveryFacts],
  );

  // Blend real supabase reviews (staff-wide) + local demo cases into "new reviews"
  const blendedNew = useMemo<NewReview[]>(() => {
    const fromReal = realReviews
      .slice(0, 6)
      .map<NewReview>((r) => reviewToNewReview(r, negByReviewId[r.id], recoveryFor(r.id)));
    const fromLocal = cases.slice(0, 2).map<NewReview>((c) => ({
      id: c.id,
      customer: c.details.vendor ? "You (test submission)" : "You",
      vendor: c.details.vendor ?? "Pending parsing",
      method: c.method === "email" ? "Email" : c.method === "photo" ? "Photo" : "Upload",
      submitted: relativeTime(c.createdAt),
      status: mapStatus(c.status),
      priority: c.fastTrack ? "high" : "medium",
      assigned: "Unassigned",
    }));
    const combined = [...fromReal, ...fromLocal];
    if (combined.length === 0) return newReviews;
    return [...combined, ...newReviews].slice(0, 8);
  }, [cases, realReviews, negByReviewId, recoveryFor]);

  const aiStates = useReviewAIStates(realReviews.map((r) => r.id).filter(Boolean));

  // Priority queue: prefer negotiations first, then freshly submitted reviews
  const blendedPriority = useMemo<PriorityItem[]>(() => {
    const withNeg = realReviews
      .filter((r) => negByReviewId[r.id])
      .map<PriorityItem>((r) => reviewToPriority(r, negByReviewId[r.id], recoveryFor(r.id)));
    const without = realReviews
      .filter((r) => !negByReviewId[r.id] && r.status !== "completed")
      .slice(0, 3)
      .map<PriorityItem>((r) => reviewToPriority(r, undefined, recoveryFor(r.id)));
    const fromReal = [...withNeg, ...without].slice(0, 6);
    if (fromReal.length === 0) return priorityQueue;
    return [...fromReal, ...priorityQueue].slice(0, 6);
  }, [realReviews, negByReviewId, recoveryFor]);

  // Filter priority queue by quick filter
  const filteredPriority = useMemo(
    () => blendedPriority.filter((p) => matchesQuickFilter(p, quickFilter)),
    [blendedPriority, quickFilter],
  );

  // Activity feed from real timeline events, with demo fallback
  const blendedActivity = useMemo<ActivityItem[]>(() => {
    const fromReal = realTimeline.slice(0, 6).map<ActivityItem>((t) => ({
      id: t.id,
      kind: mapEventKind(t.event_type),
      title: t.title,
      meta: t.description ?? `Review ${t.review_id.slice(0, 8)}`,
      time: relativeTime(t.created_at),
    }));
    if (fromReal.length === 0) return activityFeed;
    return [...fromReal, ...activityFeed].slice(0, 8);
  }, [realTimeline]);

  const negotiationStats = useMemo(() => {
    const list = Object.values(negByReviewId);
    const count = (pred: (n: Negotiation) => boolean) => list.filter(pred).length;
    return {
      requested: count((n) => n.status === "requested"),
      assigned: count((n) => n.status === "assigned"),
      negotiating: count((n) => ["negotiating", "in_progress"].includes(n.status)),
      awaitingVendor: count((n) =>
        ["vendor_contacted", "waiting_response", "waiting_vendor"].includes(n.status),
      ),
      offersReceived: count((n) => n.status === "offer_received"),
      completedToday: count(
        (n) => ["completed", "accepted"].includes(n.status) && isToday(n.updated_at),
      ),
    };
  }, [negByReviewId]);

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page space-y-10 py-10 md:py-14">
        <Breadcrumbs items={[{ label: "Operations Center" }]} />
        <WelcomeHeader />
        <GlobalSearch />
        <QuickFilters value={quickFilter} onChange={setQuickFilter} />
        {realLoading && realReviews.length === 0 ? (
          <PriorityQueueSkeleton />
        ) : (
          <PriorityQueue
            items={filteredPriority}
            totalCount={blendedPriority.length}
            isFiltered={quickFilter !== "all"}
            onClearFilter={() => setQuickFilter("all")}
          />
        )}

        <OperationsSummary stats={negotiationStats} />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] [&>*]:min-w-0">
          <NewReviewsTable rows={blendedNew} aiStates={aiStates} />
          <RecentActivity items={blendedActivity} />
        </div>

        <QuickActions />

        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <TeamWorkload members={team} />
          <PerformanceSnapshot />
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}


function PriorityQueueSkeleton() {
  return (
    <section aria-busy="true" aria-label="Loading priority queue">
      <div className="flex flex-col gap-2">
        <div className="h-3 w-40 animate-pulse rounded bg-secondary" />
        <div className="h-8 w-72 animate-pulse rounded bg-secondary" />
        <div className="h-3 w-56 animate-pulse rounded bg-secondary" />
      </div>
      <div className="mt-6 grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 space-y-2">
                <div className="h-2.5 w-32 animate-pulse rounded bg-secondary" />
                <div className="h-5 w-40 animate-pulse rounded bg-secondary" />
                <div className="h-3 w-48 animate-pulse rounded bg-secondary" />
              </div>
              <div className="h-6 w-20 animate-pulse rounded-full bg-secondary" />
            </div>
            <div className="h-12 animate-pulse rounded-xl bg-secondary" />
            <div className="grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((j) => (
                <div key={j} className="h-14 animate-pulse rounded-lg bg-secondary" />
              ))}
            </div>
            <div className="flex gap-3 pt-1">
              <div className="h-10 flex-1 animate-pulse rounded-lg bg-secondary" />
              <div className="h-10 w-24 animate-pulse rounded-lg bg-secondary" />
            </div>
          </div>
        ))}
      </div>
      <span className="sr-only">Loading priority queue…</span>
    </section>
  );
}


/* ─────────────────────── Real review adapters ─────────────────────── */

function reviewToNewReview(r: Review, neg?: Negotiation, canonicalRecovery?: number | null): NewReview {
  const method: NewReview["method"] =
    r.submission_method === "email"
      ? "Email"
      : r.submission_method === "photo"
        ? "Photo"
        : "Upload";
  return {
    id: r.file_number,
    reviewId: r.id,
    customer: `Customer #${r.customer_id.slice(0, 6)}`,
    vendor: r.vendor ?? "Pending parsing",
    method,
    submitted: relativeTime(r.created_at),
    status: mapStatus(mapDbStatusToCase(r.status)),
    priority: r.fast_track ? "high" : r.urgency === "high" ? "high" : "medium",
    assigned: r.assigned_staff_id ? "Assigned" : "Unassigned",
    negotiation: neg ? negotiationBrief(neg, r, canonicalRecovery ?? null) : undefined,
  };
}

function reviewToPriority(
  r: Review,
  neg?: Negotiation,
  canonicalRecovery?: number | null,
): PriorityItem {
  const progressMap: Record<string, number> = {
    received: 15,
    reviewing: 40,
    report_ready: 90,
    self_serve: 70,
    negotiating: 75,
    waiting_vendor: 80,
    offer_received: 88,
    completed: 100,
  };
  return {
    id: r.file_number,
    customer: `Customer #${r.customer_id.slice(0, 6)}`,
    vendor: r.vendor ?? "Pending parsing",
    status: mapStatus(mapDbStatusToCase(r.status)),
    urgency: r.fast_track ? "high" : r.urgency === "high" ? "high" : "medium",
    submitted: relativeTime(r.created_at),
    progress: progressMap[r.status] ?? 25,
    savings: supportedRecoveryDisplay(canonicalRecovery ?? null),
    specialist: r.assigned_staff_id ? "Assigned" : "Unassigned",
    negotiation: neg ? negotiationBrief(neg, r, canonicalRecovery ?? null) : undefined,
  };
}

const NEG_STAGE_LABEL: Record<string, string> = {
  requested: "Requested",
  assigned: "Assigned",
  preparing: "Preparing",
  vendor_contacted: "Vendor Contacted",
  waiting_response: "Awaiting Vendor",
  waiting_vendor: "Awaiting Vendor",
  negotiating: "Negotiating",
  in_progress: "Negotiating",
  offer_received: "Offer Received",
  completed: "Completed",
  accepted: "Completed",
};

function negotiationBrief(
  n: Negotiation,
  r: Review,
  canonicalRecovery: number | null,
): NegotiationBrief {
  const daysOpen = Math.max(
    0,
    Math.floor((Date.now() - new Date(n.created_at).getTime()) / 86_400_000),
  );
  const originalRaw = n.original_amount ?? r.total_amount ?? null;
  const original = originalRaw == null ? null : Number(originalRaw);
  const settlement = n.settlement_amount == null ? null : Number(n.settlement_amount);
  const financial = resolveNegotiationFinancial({
    originalAmount: original,
    settlementAmount: settlement,
    canonicalRecovery,
  });
  return {
    id: n.id,
    number: n.negotiation_number ?? n.id.slice(0, 8),
    status: n.status,
    stage: NEG_STAGE_LABEL[n.status] ?? prettyStageFromKey(n.status),
    specialist: n.assigned_specialist_id ? "Assigned" : "Unassigned",
    daysOpen,
    financial,
  };
}

function prettyStageFromKey(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapEventKind(eventType: string): ActivityItem["kind"] {
  const t = eventType.toLowerCase();
  if (t.includes("purchas") || t.includes("negotiation_requested")) return "neg_purchased";
  if (t.includes("assign") && t.includes("special")) return "neg_assigned";
  if (t.includes("specialist")) return "neg_assigned";
  if (t.includes("vendor_contact")) return "vendor_contacted";
  if (t.includes("vendor_repl") || t.includes("vendor_response")) return "vendor_replied";
  if (t.includes("settlement")) return "settlement";
  if (t.includes("negotiation_complete") || t.includes("negotiation_done"))
    return "neg_completed";
  if (t.includes("submit")) return "submitted";
  if (t.includes("report")) return "report";
  if (t.includes("negot")) return "negotiation";
  if (t.includes("vendor")) return "vendor";
  if (t.includes("customer")) return "customer";
  if (t.includes("complete")) return "completed";
  return "submitted";
}

// Keep imports referenced
void (null as unknown as TimelineEvent);


/* ─────────────────────────────────────────────────────────── */
/* Header + search                                             */
/* ─────────────────────────────────────────────────────────── */

function WelcomeHeader() {
  const { greeting, dateLabel } = useMemo(() => {
    const d = new Date();
    const h = d.getHours();
    const g = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
    return {
      greeting: g,
      dateLabel: d.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    };
  }, []);

  return (
    <section className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          Operations Center
        </div>
        <h1 className="mt-3 font-display text-4xl leading-tight text-navy md:text-5xl">
          {greeting}, Thomas.
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Manage reviews, negotiations, and customer activity from one organized workspace.{" "}
          <span className="text-navy/70">{dateLabel}</span>
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3 md:self-end">
        <button
          type="button"
          onClick={() =>
            toast("Manual review draft started", {
              description: "Full manual entry form coming soon.",
            })
          }
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-semibold text-navy shadow-sm transition hover:-translate-y-0.5 hover:shadow-elevated"
        >
          <Plus className="h-4 w-4" />
          Create Manual Review
        </button>
        <button
          type="button"
          onClick={() =>
            document.querySelector<HTMLInputElement>('input[placeholder^="Search by"]')?.focus()
          }
          className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-navy/90 hover:shadow-elevated"
        >
          <Search className="h-4 w-4" />
          Search Reviews
        </button>
      </div>
    </section>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <label className="flex items-center gap-3 rounded-xl border border-transparent bg-secondary/50 px-4 py-3 transition focus-within:border-navy/20 focus-within:bg-card">
        <Search className="h-5 w-5 text-navy/60" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by file number, customer, vendor, invoice number, or reference…"
          className="w-full bg-transparent text-sm text-navy placeholder:text-muted-foreground focus:outline-none"
        />
        <kbd className="hidden rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground md:inline">
          ⌘K
        </kbd>
      </label>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Priority queue                                              */
/* ─────────────────────────────────────────────────────────── */

function PriorityQueue({
  items,
  totalCount,
  isFiltered,
  onClearFilter,
}: {
  items: PriorityItem[];
  totalCount: number;
  isFiltered: boolean;
  onClearFilter: () => void;
}) {
  return (
    <section>
      <SectionHeader
        eyebrow="Today's Priority Queue"
        title="What needs your attention right now"
        description={
          isFiltered
            ? `Showing ${items.length} of ${totalCount} priority reviews matching this filter.`
            : "The highest-priority reviews across the team."
        }
        icon={<Flame className="h-5 w-5" />}
      />
      {items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/60 p-10">
          <EmptyRow
            icon={<Inbox className="h-5 w-5" />}
            title={isFiltered ? "No reviews match this filter" : "Priority queue is clear"}
            body={
              isFiltered
                ? "Try a different quick filter, or clear it to see every priority review."
                : "New submissions and negotiations will appear here as they arrive. Nothing needs your attention right now."
            }
          />
          {isFiltered && (
            <div className="mt-2 flex justify-center">
              <button
                type="button"
                onClick={onClearFilter}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold text-navy transition hover:bg-secondary"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <PriorityCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}


function PriorityCard({ item }: { item: PriorityItem }) {
  const neg = item.negotiation;
  return (
    <article className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            {item.id}
          </div>
          <div className="mt-2 font-display text-lg text-navy">{item.customer}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" />
            {item.vendor}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {neg && (
            <span className="inline-flex items-center gap-1 rounded-full border border-negotiate/20 bg-negotiate-soft px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-negotiate">
              <Handshake className="h-3 w-3" />
              Expert Negotiation
            </span>
          )}
          <UrgencyBadge urgency={item.urgency} />
        </div>
      </div>

      {neg ? (
        <div className="mt-5 rounded-xl border border-negotiate/20 bg-negotiate-soft px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-negotiate">
              <CircleDot className="h-3.5 w-3.5" />
              {neg.stage}
            </div>
            <span className="font-mono text-[11px] text-negotiate/80">{neg.number}</span>
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-xl bg-secondary/50 px-4 py-3 text-sm text-navy">
          <div className="flex items-center gap-2">
            <CircleDot className="h-3.5 w-3.5 text-navy/60" />
            {item.status}
          </div>
        </div>
      )}

      {!neg && (
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span>AI Progress</span>
            <span className="text-navy">{item.progress}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-navy" style={{ width: `${item.progress}%` }} />
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
        <MetaTile
          label={neg ? "Days Open" : "Submitted"}
          value={neg ? `${neg.daysOpen}d` : item.submitted}
          icon={<Clock className="h-3.5 w-3.5" />}
        />
        <MetaTile
          label={neg ? neg.financial.label : SURFACE_LABELS.supportedRecovery}
          value={neg ? neg.financial.value : item.savings}
          icon={<Sparkles className="h-3.5 w-3.5" />}
          accent="savings"
        />
        <MetaTile
          label="Specialist"
          value={neg ? neg.specialist : item.specialist}
          icon={<UserRound className="h-3.5 w-3.5" />}
        />
        <MetaTile
          label={neg ? "Negotiation #" : "Urgency"}
          value={
            neg
              ? neg.number
              : item.urgency === "high"
                ? "High"
                : item.urgency === "medium"
                  ? "Medium"
                  : "Low"
          }
          icon={neg ? <Handshake className="h-3.5 w-3.5" /> : <Flame className="h-3.5 w-3.5" />}
        />
      </div>

      <div className="mt-6 flex items-center gap-3">
        {neg ? (
          <>
            <Link
              to="/operations/negotiations/$id"
              params={{ id: neg.id }}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-negotiate px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-negotiate/90"
            >
              Open Negotiation
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/workspace/$id"
              params={{ id: item.id }}
              className="inline-flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-semibold text-navy transition hover:bg-secondary/60"
            >
              Open Review
            </Link>
          </>
        ) : (
          <>
            <Link
              to="/workspace/$id"
              params={{ id: item.id }}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-navy/90"
            >
              Open Review
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </Link>
            <button
              type="button"
              onClick={() =>
                toast.success("Assignment updated", {
                  description: `${item.id} assigned to Thomas W.`,
                })
              }
              className="inline-flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-semibold text-navy transition hover:bg-secondary/60"
            >
              Assign
            </button>
          </>
        )}
      </div>
    </article>
  );
}


function MetaTile({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent?: "savings";
}) {
  return (
    <div className="rounded-lg border border-border/70 bg-card px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 text-sm font-semibold ${accent === "savings" ? "text-savings" : "text-navy"}`}
      >
        {value}
      </div>
    </div>
  );
}

function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  const map: Record<Urgency, { label: string; cls: string }> = {
    high: { label: "High Priority", cls: "bg-red-50 text-red-700 border-red-100" },
    medium: {
      label: "Medium",
      cls: "bg-amber-50 text-amber-700 border-amber-100",
    },
    low: {
      label: "Low",
      cls: "bg-secondary text-navy/70 border-border",
    },
  };
  const s = map[urgency];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Operations summary                                          */
/* ─────────────────────────────────────────────────────────── */

interface NegotiationStats {
  requested: number;
  assigned: number;
  negotiating: number;
  awaitingVendor: number;
  offersReceived: number;
  completedToday: number;
}

function OperationsSummary({ stats }: { stats: NegotiationStats }) {
  const cards = [
    { label: "Requested", value: stats.requested, icon: <Inbox className="h-4 w-4" />, tone: "negotiate" },
    { label: "Assigned", value: stats.assigned, icon: <UserRound className="h-4 w-4" />, tone: "trust" },
    { label: "Negotiating", value: stats.negotiating, icon: <Handshake className="h-4 w-4" />, tone: "amber" },
    { label: "Awaiting Vendor", value: stats.awaitingVendor, icon: <Building2 className="h-4 w-4" />, tone: "amber" },
    { label: "Offers Received", value: stats.offersReceived, icon: <DollarSign className="h-4 w-4" />, tone: "savings" },
    { label: "Completed Today", value: stats.completedToday, icon: <CheckCircle2 className="h-4 w-4" />, tone: "savings" },
  ] as const;

  const toneMap: Record<string, string> = {
    navy: "bg-navy/10 text-navy",
    trust: "bg-blue-50 text-blue-700",
    negotiate: "bg-negotiate-soft text-negotiate",
    amber: "bg-amber-50 text-amber-700",
    savings: "bg-emerald-50 text-emerald-700",
  };

  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-2xl border border-border bg-card p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated"
        >
          <div
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[c.tone]}`}
          >
            {c.icon}
          </div>
          <div className="mt-4 font-display text-3xl text-navy">{c.value}</div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {c.label}
          </div>
        </div>
      ))}
    </section>
  );
}


/* ─────────────────────────────────────────────────────────── */
/* New reviews table                                           */
/* ─────────────────────────────────────────────────────────── */

function NewReviewsTable({
  rows,
  aiStates,
}: {
  rows: NewReview[];
  aiStates?: Record<string, ReviewAIState>;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card shadow-card">
      <div className="flex items-center justify-between gap-3 border-b border-border/70 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/10 text-navy">
            <Inbox className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-lg text-navy">New Reviews</div>
            <div className="text-xs text-muted-foreground">
              Newly submitted invoices awaiting triage.
            </div>
          </div>
        </div>
        <button
          type="button"
          className="hidden text-xs font-semibold text-navy hover:underline sm:inline-flex"
        >
          View all
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyRow
          icon={<Inbox className="h-5 w-5" />}
          title="No new reviews"
          body="You're all caught up. New submissions will appear here."
        />
      ) : (
        <>
          {/* desktop */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/70 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-6 py-3 text-left">File</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-left">Method</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Negotiation #</th>
                  <th className="px-4 py-3 text-left">Stage</th>
                  <th className="px-4 py-3 text-left">Specialist</th>
                  <th className="px-4 py-3 text-left">Priority</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-border/50 last:border-0 hover:bg-secondary/40"
                  >
                    <td className="px-6 py-4 font-mono text-xs text-navy">{r.id}</td>
                    <td className="px-4 py-4 text-navy">{r.customer}</td>
                    <td className="px-4 py-4 text-muted-foreground">{r.vendor}</td>
                    <td className="px-4 py-4">
                      <MethodChip method={r.method} />
                    </td>
                    <td className="px-4 py-4 text-muted-foreground">{r.submitted}</td>
                    <td className="px-4 py-4 text-navy">
                      <div className="flex items-center gap-2">
                        <span>{r.status}</span>
                        <AIStateBadge state={r.reviewId ? aiStates?.[r.reviewId] : undefined} />
                      </div>
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-negotiate">
                      {r.negotiation?.number ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      {r.negotiation ? (
                        <NegotiationStageChip status={r.negotiation.status} label={r.negotiation.stage} />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-muted-foreground">
                      {r.negotiation ? r.negotiation.specialist : r.assigned}
                    </td>
                    <td className="px-4 py-4">
                      <UrgencyBadge urgency={r.priority} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      {r.negotiation ? (
                        <Link
                          to="/operations/negotiations/$id"
                          params={{ id: r.negotiation.id }}
                          className="inline-flex items-center gap-1 rounded-md bg-negotiate px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-negotiate/90"
                        >
                          Negotiation
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      ) : (
                        <Link
                          to="/workspace/$id"
                          params={{ id: r.id }}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-navy transition hover:bg-secondary/60"
                        >
                          Open
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          </div>

          {/* mobile */}
          <div className="divide-y divide-border/60 md:hidden">
            {rows.map((r) => (
              <div key={r.id} className="space-y-2 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-navy">{r.id}</div>
                  <UrgencyBadge urgency={r.priority} />
                </div>
                <div className="font-semibold text-navy">{r.customer}</div>
                <div className="text-sm text-muted-foreground">{r.vendor}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MethodChip method={r.method} />
                  <span>•</span>
                  <span>{r.submitted}</span>
                </div>
                <Link
                  to="/workspace/$id"
                  params={{ id: r.id }}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-navy"
                >
                  Open Review <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function MethodChip({ method }: { method: NewReview["method"] }) {
  const map = {
    Photo: { icon: <Upload className="h-3 w-3" />, cls: "bg-blue-50 text-blue-700" },
    Upload: { icon: <FileText className="h-3 w-3" />, cls: "bg-navy/10 text-navy" },
    Email: { icon: <MailPlus className="h-3 w-3" />, cls: "bg-negotiate-soft text-negotiate" },
  } as const;
  const s = map[method];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}
    >
      {s.icon}
      {method}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Recent activity                                             */
/* ─────────────────────────────────────────────────────────── */

function RecentActivity({ items }: { items: ActivityItem[] }) {
  const iconMap: Record<ActivityItem["kind"], { icon: React.ReactNode; cls: string }> = {
    submitted: {
      icon: <Inbox className="h-4 w-4" />,
      cls: "bg-navy/10 text-navy",
    },
    report: {
      icon: <Sparkles className="h-4 w-4" />,
      cls: "bg-blue-50 text-blue-700",
    },
    negotiation: {
      icon: <Handshake className="h-4 w-4" />,
      cls: "bg-negotiate-soft text-negotiate",
    },
    vendor: {
      icon: <Building2 className="h-4 w-4" />,
      cls: "bg-amber-50 text-amber-700",
    },
    customer: {
      icon: <UserRound className="h-4 w-4" />,
      cls: "bg-blue-50 text-blue-700",
    },
    completed: {
      icon: <BadgeCheck className="h-4 w-4" />,
      cls: "bg-emerald-50 text-emerald-700",
    },
    neg_purchased: {
      icon: <DollarSign className="h-4 w-4" />,
      cls: "bg-negotiate-soft text-negotiate",
    },
    neg_assigned: {
      icon: <UserRound className="h-4 w-4" />,
      cls: "bg-blue-50 text-blue-700",
    },
    vendor_contacted: {
      icon: <Send className="h-4 w-4" />,
      cls: "bg-amber-50 text-amber-700",
    },
    vendor_replied: {
      icon: <MessageSquare className="h-4 w-4" />,
      cls: "bg-orange-50 text-orange-700",
    },
    settlement: {
      icon: <DollarSign className="h-4 w-4" />,
      cls: "bg-emerald-50 text-emerald-700",
    },
    neg_completed: {
      icon: <BadgeCheck className="h-4 w-4" />,
      cls: "bg-emerald-50 text-emerald-700",
    },
  };


  return (
    <section className="rounded-2xl border border-border bg-card shadow-card">
      <div className="flex items-center gap-3 border-b border-border/70 px-6 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/10 text-navy">
          <Activity className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg text-navy">Recent Activity</div>
          <div className="text-xs text-muted-foreground">Live across the team.</div>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyRow
          icon={<Activity className="h-5 w-5" />}
          title="No recent activity"
          body="Team events will show up here as they happen."
        />
      ) : (
        <ol className="relative divide-y divide-border/60">
          {items.map((it) => {
            const s = iconMap[it.kind];
            return (
              <li key={it.id} className="flex gap-3 px-6 py-4">
                <div
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${s.cls}`}
                >
                  {s.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-navy">{it.title}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">{it.meta}</div>
                </div>
                <div className="whitespace-nowrap text-[11px] text-muted-foreground">{it.time}</div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Quick actions                                               */
/* ─────────────────────────────────────────────────────────── */

function QuickActions() {
  const actions = [
    {
      title: "Review Queue",
      body: "Work through in-progress reviews.",
      icon: <ClipboardList className="h-5 w-5" />,
      tone: "bg-navy/10 text-navy",
    },
    {
      title: "Negotiations",
      body: "Active expert negotiations.",
      icon: <Handshake className="h-5 w-5" />,
      tone: "bg-negotiate-soft text-negotiate",
      to: "/operations/negotiations" as const,
    },
    {
      title: "Reports Ready",
      body: "Findings awaiting QA & delivery.",
      icon: <Sparkles className="h-5 w-5" />,
      tone: "bg-blue-50 text-blue-700",
    },
    {
      title: "Search Reviews",
      body: "Find any case by file, customer, or vendor.",
      icon: <FolderSearch className="h-5 w-5" />,
      tone: "bg-navy/10 text-navy",
    },
    {
      title: "Templates",
      body: "Negotiation letters & findings templates.",
      icon: <BookOpen className="h-5 w-5" />,
      tone: "bg-emerald-50 text-emerald-700",
    },
    {
      title: "Customer Directory",
      body: "Browse and manage customer accounts.",
      icon: <Users className="h-5 w-5" />,
      tone: "bg-amber-50 text-amber-700",
    },
  ];

  return (
    <section>
      <SectionHeader
        eyebrow="Quick Actions"
        title="Shortcuts to the tools you use daily"
        icon={<ArrowRight className="h-5 w-5" />}
      />
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((a) => {
          const inner = (
            <>
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${a.tone}`}>
                {a.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display text-base text-navy">{a.title}</div>
                  <ArrowRight className="h-4 w-4 text-navy/50 transition group-hover:translate-x-0.5 group-hover:text-navy" />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">{a.body}</div>
              </div>
            </>
          );
          const cls =
            "group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 text-left shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated";
          if ("to" in a && a.to) {
            return (
              <Link key={a.title} to={a.to} className={cls}>
                {inner}
              </Link>
            );
          }
          return (
            <button
              key={a.title}
              type="button"
              onClick={() =>
                toast(a.title, { description: `${a.body} — coming soon.` })
              }
              className={cls}
            >
              {inner}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Team workload                                               */
/* ─────────────────────────────────────────────────────────── */

function TeamWorkload({ members }: { members: TeamMember[] }) {
  const statusTone: Record<TeamMember["status"], string> = {
    Available: "bg-emerald-50 text-emerald-700",
    "In Review": "bg-blue-50 text-blue-700",
    "On Call": "bg-negotiate-soft text-negotiate",
    Away: "bg-secondary text-navy/70",
  };

  return (
    <section className="rounded-2xl border border-border bg-card shadow-card">
      <div className="flex items-center gap-3 border-b border-border/70 px-6 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/10 text-navy">
          <Users className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg text-navy">Team Workload</div>
          <div className="text-xs text-muted-foreground">Current assignments across the team.</div>
        </div>
      </div>
      <div className="grid gap-px bg-border/60 sm:grid-cols-2">
        {members.map((m) => (
          <div key={m.name} className="bg-card p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
                {m.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-navy">{m.name}</div>
                <span
                  className={`mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone[m.status]}`}
                >
                  {m.status}
                </span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <MiniStat label="Reviews" value={m.reviews} />
              <MiniStat label="Negot." value={m.negotiations} />
              <MiniStat label="Done" value={m.completed} tone="savings" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: number; tone?: "savings" }) {
  return (
    <div className="rounded-lg bg-secondary/50 py-2">
      <div className={`font-display text-lg ${tone === "savings" ? "text-savings" : "text-navy"}`}>
        {value}
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Performance snapshot                                        */
/* ─────────────────────────────────────────────────────────── */

function PerformanceSnapshot() {
  const rows = [
    {
      label: "Reviews Completed Today",
      value: performance.completedToday,
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      label: "Average Review Time",
      value: performance.avgReviewTime,
      icon: <Timer className="h-4 w-4" />,
    },
    {
      label: "Reports Delivered",
      value: performance.reportsDelivered,
      icon: <FileText className="h-4 w-4" />,
    },
    {
      label: "Negotiations Completed",
      value: performance.negotiationsCompleted,
      icon: <Handshake className="h-4 w-4" />,
    },
    {
      label: "Customer Responses Waiting",
      value: performance.responsesWaiting,
      icon: <AlertTriangle className="h-4 w-4" />,
    },
  ];

  return (
    <section className="rounded-2xl border border-border bg-card shadow-card">
      <div className="flex items-center gap-3 border-b border-border/70 px-6 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/10 text-navy">
          <Activity className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg text-navy">Performance Snapshot</div>
          <div className="text-xs text-muted-foreground">Today at a glance.</div>
        </div>
      </div>
      <ul className="divide-y divide-border/60">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-3 px-6 py-3.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-navy/70">
              {r.icon}
            </div>
            <div className="min-w-0 flex-1 text-sm text-navy">{r.label}</div>
            <div className="font-display text-lg text-navy">{r.value}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Shared bits                                                 */
/* ─────────────────────────────────────────────────────────── */

function SectionHeader({
  eyebrow,
  title,
  description,
  icon,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
      <div>
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          {icon}
          {eyebrow}
        </div>
        <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">{title}</h2>
        {description && <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

function EmptyRow({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-navy/70">
        {icon}
      </div>
      <div className="font-display text-base text-navy">{title}</div>
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Utils                                                       */
/* ─────────────────────────────────────────────────────────── */

function mapStatus(s: CaseStatus): string {
  switch (s) {
    case "awaiting_payment":
      return "Awaiting payment";
    case "received":
      return "Received";
    case "reviewing":
      return "AI parsing";
    case "report_ready":
      return "Ready for review";
    case "self_serve":
      return "Ready to negotiate";
    case "negotiating":
      return "Negotiating";
    case "waiting_vendor":
      return "Waiting on vendor";
    case "offer_received":
      return "Offer received";
    case "completed":
      return "Completed";
    case "action_required":
      return "Waiting for missing documents";
    case "awaiting_customer":
      return "Awaiting customer";
    case "pipeline_blocked":
      return "Needs attention";

  }
}

function isToday(iso: string) {
  const d = new Date(iso);
  const n = new Date();
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  );
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function AIStateBadge({ state }: { state?: ReviewAIState }) {
  if (!state || state === "none") return null;
  const map: Record<Exclude<ReviewAIState, "none">, { label: string; cls: string }> = {
    pending: { label: "AI Pending", cls: "bg-secondary text-muted-foreground" },
    processing: { label: "AI Processing", cls: "bg-trust-soft text-trust" },
    failed: { label: "AI Failed", cls: "bg-warn/10 text-warn" },
    complete: { label: "AI Complete", cls: "bg-savings-soft text-savings" },
  };
  const s = map[state];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

// Silence unused import warnings for icons kept for future use
void currency;
void buildReport;
void Mail;
void FilterIcon;
export type _Case = Case;

/* ─────────────────────── Negotiation stage chip ─────────────────────── */

function NegotiationStageChip({ status, label }: { status: string; label: string }) {
  const styles: Record<string, string> = {
    requested: "bg-negotiate-soft text-negotiate border-negotiate/20",
    assigned: "bg-blue-50 text-blue-700 border-blue-200",
    preparing: "bg-blue-50 text-blue-700 border-blue-200",
    vendor_contacted: "bg-amber-50 text-amber-700 border-amber-200",
    waiting_response: "bg-amber-50 text-amber-700 border-amber-200",
    waiting_vendor: "bg-amber-50 text-amber-700 border-amber-200",
    negotiating: "bg-orange-50 text-orange-700 border-orange-200",
    in_progress: "bg-orange-50 text-orange-700 border-orange-200",
    offer_received: "bg-green-50 text-green-700 border-green-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const cls = styles[status] ?? "bg-secondary text-navy border-border";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${cls}`}
    >
      <CircleDot className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

/* ─────────────────────── Quick filters ─────────────────────── */

type QuickFilter =
  | "all"
  | "needs_assignment"
  | "waiting_customer"
  | "waiting_vendor"
  | "offers_ready"
  | "high_value"
  | "urgent";

const QUICK_FILTERS: Array<{ key: QuickFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "needs_assignment", label: "Needs Assignment" },
  { key: "waiting_customer", label: "Waiting Customer" },
  { key: "waiting_vendor", label: "Waiting Vendor" },
  { key: "offers_ready", label: "Offers Ready" },
  { key: "high_value", label: "High Value" },
  { key: "urgent", label: "Urgent" },
];

function QuickFilters({
  value,
  onChange,
}: {
  value: QuickFilter;
  onChange: (v: QuickFilter) => void;
}) {
  const active = QUICK_FILTERS.find((f) => f.key === value);
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="mr-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground"
          aria-hidden="true"
        >
          <FilterIcon className="h-3.5 w-3.5" />
          Quick Filters
        </div>
        <div
          role="group"
          aria-label="Quick filters"
          className="flex flex-wrap items-center gap-2"
        >
          {QUICK_FILTERS.map((f) => {
            const isActive = value === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => onChange(f.key)}
                aria-pressed={isActive}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-negotiate focus-visible:ring-offset-2 ${
                  isActive
                    ? "border-negotiate bg-negotiate text-white shadow-sm"
                    : "border-border bg-card text-navy hover:bg-secondary"
                }`}
              >
                {isActive && <CheckCircle2 className="h-3 w-3" aria-hidden="true" />}
                {f.label}
              </button>
            );
          })}
        </div>
        {value !== "all" && (
          <button
            type="button"
            onClick={() => onChange("all")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-semibold text-navy transition hover:bg-secondary"
          >
            Clear ({active?.label})
          </button>
        )}
      </div>
    </section>
  );
}


function matchesQuickFilter(p: PriorityItem, f: QuickFilter): boolean {
  const neg = p.negotiation;
  switch (f) {
    case "all":
      return true;
    case "needs_assignment":
      return neg ? neg.specialist === "Unassigned" : p.specialist === "Unassigned";
    case "waiting_customer":
      return /wait/i.test(p.status) && /customer/i.test(p.status);
    case "waiting_vendor":
      return neg
        ? ["vendor_contacted", "waiting_response", "waiting_vendor"].includes(neg.status)
        : /vendor/i.test(p.status);
    case "offers_ready":
      return neg?.status === "offer_received";
    case "high_value":
      return (neg?.financial.amount ?? 0) >= 1000;
    case "urgent":
      return p.urgency === "high";
  }
}

